import { snakelize } from '@actions/string';
import Chip from '@components/ChoiceGroup/Chip';
import type { Choice } from '@components/ChoiceGroup/domain';
import type Icon from '@components/Icon';
import type { Catalog } from '@core/catalog';
import { findFormation, getDomainFormations } from '@core/theme';
import type { ComponentProps } from 'react';
import { formationTypes, type FormationType } from './formationType';
import {
  ALTERNANCE_FINANCEMENT_CODE,
  CPF_FINANCEMENT_CODE,
  delaiDebutOptions,
  domaineExpertiseOptions,
  equipementInfoOptions,
  etudesOptions,
  experienceEsthetiqueOptions,
  financementOptions,
  formationModeOptions,
  formationPetiteEnfanceOptions,
  genderOptions,
  ideeProjetOptions,
  INTERETS_ESTHETIQUE_EXCLUSIVE,
  interetsEsthetiqueOptions,
  niveauAnglaisOptions,
  objectifOptions,
  professionalSituationOptions,
  REDIRECTION_URLS,
  secteurActiviteOptions,
  sourcesOptions,
  stageOptions,
  transmettreCompetencesOptions,
  typeStructureOptions,
  UNCOVERED_DOMAIN_ID,
} from './lists';

export const STEP_ORDER = [
  'situationPro',
  'objectif',
  'domain',
  'training',
  'niveauEtudes',
  'financement',
  'formationMode',
  'ideeProjet',
  'typeStructure',
  'delaiDebut',
  'secteurActivite',
  'domaineExpertise',
  'transmettreCompetences',
  'experienceEsthetique',
  'interetsEsthetique',
  'stageEsthetique',
  'formationPetiteEnfance',
  'stagePetiteEnfance',
  'equipementInfo',
  'niveauAnglais',
  'mailConsent',
  'personalConsent',
  'sourceId',
] as const;

export type StepKey = (typeof STEP_ORDER)[number];

export interface StepDefinition {
  key: StepKey;
  title: string;
  subtext?: string;
}

export const formSteps: Record<StepKey, StepDefinition> = {
  situationPro: {
    key: 'situationPro',
    title: 'Quelle est votre situation professionnelle ?',
  },
  objectif: {
    key: 'objectif',
    title: 'Quel est votre principal objectif ?',
    subtext:
      'Nous avons à cœur de connaître votre projet pour vous guider au mieux !',
  },
  domain: {
    key: 'domain',
    title: 'Dans quel domaine souhaitez-vous vous former ?',
  },
  training: {
    key: 'training',
    title: 'Quelle formation vous intéresse ?',
  },
  niveauEtudes: {
    key: 'niveauEtudes',
    title:
      'Renseignez votre diplôme le plus élevé au moment de débuter votre formation',
    subtext: 'Renseignez votre diplôme le plus élevé',
  },
  financement: {
    key: 'financement',
    title: 'Comment comptez-vous financer votre formation ?',
    subtext:
      'Nos conseillers en formation vous orientent vers les meilleures solutions de financement.',
  },
  formationMode: {
    key: 'formationMode',
    title: 'Comment souhaitez-vous vous former ?',
  },
  ideeProjet: {
    key: 'ideeProjet',
    title: "Avez-vous déjà une idée de projet de création d'entreprise ?",
  },
  typeStructure: {
    key: 'typeStructure',
    title: 'Quel type de structure souhaitez-vous créer ?',
  },
  delaiDebut: {
    key: 'delaiDebut',
    title: 'Quand souhaitez-vous débuter ?',
  },
  secteurActivite: {
    key: 'secteurActivite',
    title: 'Dans quel secteur souhaitez-vous vous lancer ?',
  },
  domaineExpertise: {
    key: 'domaineExpertise',
    title: "Avez-vous un domaine d'expertise ?",
  },
  transmettreCompetences: {
    key: 'transmettreCompetences',
    title:
      'Souhaitez-vous transmettre vos compétences en devenant formateur(trice) ?',
  },
  experienceEsthetique: {
    key: 'experienceEsthetique',
    title: "Avez-vous déjà travaillé dans l'esthétique ou la beauté ?",
  },
  interetsEsthetique: {
    key: 'interetsEsthetique',
    title: "Qu'est-ce qui vous intéresse dans l'esthétique ?",
    subtext: 'Plusieurs réponses possibles',
  },
  stageEsthetique: {
    key: 'stageEsthetique',
    title:
      'Un stage pratique de 12 semaines est obligatoire pour valider le CAP - êtes-vous en mesure de faire ce stage ?',
  },
  formationPetiteEnfance: {
    key: 'formationPetiteEnfance',
    title: 'Par quelle formation êtes-vous intéressé(e) ?',
  },
  stagePetiteEnfance: {
    key: 'stagePetiteEnfance',
    title:
      'Un stage pratique de 14 à 16 semaines est obligatoire pour valider le CAP - êtes-vous en mesure de faire ce stage ?',
  },
  equipementInfo: {
    key: 'equipementInfo',
    title:
      'Votre ordinateur a-t-il au moins 16 Go de RAM et tourne-t-il sous Windows 10/11 ou macOS ?',
  },
  niveauAnglais: {
    key: 'niveauAnglais',
    title: "Quel est votre niveau d'anglais ?",
  },
  mailConsent: {
    key: 'mailConsent',
    title: 'À quel email souhaitez-vous recevoir votre documentation ?',
    subtext:
      'Nous souhaitons vous accompagner le mieux possible dans votre projet, notre documentation vous sera transmise par mail.',
  },
  personalConsent: {
    key: 'personalConsent',
    title: 'Faisons connaissance avant de vous envoyer votre documentation',
    subtext:
      'Remplissez vos coordonnées pour pouvoir être recontacté(e) et accompagné(e) dans votre projet',
  },
  sourceId: {
    key: 'sourceId',
    title: "Dernière étape, comment avez-vous connu l'Ecole Française ?",
  },
};

interface TextInput {
  name: string;
  inputType: 'email' | 'tel' | 'name';
  label?: string;
  sublabel?: string;
  placeholder?: string;
}

export interface ChoiceFieldDef {
  kind: 'choice';
  name: string;
  choices: Choice<string>[];
  layout?: 'row' | 'grid' | 'gridPairs' | 'column'; // disposition — traduite par le parent (StepElement)
}

export interface MultipleChoiceFieldDef {
  kind: 'multipleChoice';
  name: string;
  choices: Choice<string>[];
  layout?: 'row' | 'grid' | 'gridPairs' | 'column';
  standaloneValues?: string[];
}

export interface FlexInputFieldDef {
  kind: 'flex';
  name: string;
  components: TextInput[];
}

export interface ConsentFieldDef {
  kind: 'consent';
  name: string;
  label: string;
  required: boolean;
}

export type FieldDefinition =
  | ChoiceFieldDef
  | MultipleChoiceFieldDef
  | FlexInputFieldDef
  | ConsentFieldDef;

interface FormRuntime {
  catalog: Catalog;
  answers: Record<string, unknown>;
  onChange?: (data: { name: string; value: unknown }) => void;
}

interface RawOption {
  id: string;
  name: string;
  icon?: string;
  silencedText?: string;
  condition?: string;
}

interface RawIconOption extends RawOption {
  icon: string;
  color?: ComponentProps<typeof Icon>['color'];
  fill?: boolean;
  size?: ComponentProps<typeof Icon>['size'];
  variant?: ComponentProps<typeof Icon>['variant'];
  selected?: {
    icon?: string;
    color?: ComponentProps<typeof Icon>['color'];
  };
}

const DEFAULT_ICON_CHOICE = {
  color: 'mediumGrey' as const,
  selected: { color: 'success' as const },
};

const isRawIconOption = (
  option: RawOption | RawIconOption,
): option is RawIconOption => 'color' in option || 'selected' in option;

const BRANCH_CONDITIONS = new Set<string>(Object.keys(REDIRECTION_URLS));

const toChoice =
  (ctx: FormRuntime) =>
  (option: RawOption): Choice<string> => {
    const base = {
      label: option.name,
      value: option.id,
      silencedText: option.silencedText,
    };
    const branch =
      option.condition && BRANCH_CONDITIONS.has(option.condition)
        ? option.condition
        : undefined;
    const childName =
      option.condition && !branch ? option.condition : undefined;

    if (childName) {
      return {
        ...base,
        type: 'emojiInput',
        icon: option.icon ?? '',
        input: {
          name: childName,
          value: String(ctx.answers[childName] ?? ''),
          onChange: (value) => ctx.onChange?.({ name: childName, value }),
          validate: (value) => (value ? { valid: true } : { valid: false }),
        },
      };
    }
    if (option.icon) {
      return { ...base, type: 'emoji', icon: option.icon, branch };
    }
    return { ...base, type: 'plain', branch };
  };

const toIconChoice =
  (ctx: FormRuntime) =>
  (option: RawIconOption): Choice<string> => {
    const base = {
      label: option.name,
      value: option.id,
      silencedText: option.silencedText,
    };
    const branch =
      option.condition && BRANCH_CONDITIONS.has(option.condition)
        ? option.condition
        : undefined;
    const childName =
      option.condition && !branch ? option.condition : undefined;
    const useDefaults =
      option.selected === undefined &&
      (option.color === undefined || option.color === 'inherit');
    const icon = {
      name: option.icon,
      color: useDefaults
        ? DEFAULT_ICON_CHOICE.color
        : (option.color ?? DEFAULT_ICON_CHOICE.color),
      fill: option.fill ?? true,
      size: option.size ?? 'lg',
      variant: option.variant,
      selected: useDefaults ? DEFAULT_ICON_CHOICE.selected : option.selected,
    };

    if (childName) {
      return {
        ...base,
        ...icon,
        type: 'iconInput',
        input: {
          name: childName,
          value: String(ctx.answers[childName] ?? ''),
          onChange: (value) => ctx.onChange?.({ name: childName, value }),
          validate: (value) => (value ? { valid: true } : { valid: false }),
        },
      };
    }
    return { ...base, ...icon, type: 'icon', branch };
  };

const toFieldChoice =
  (ctx: FormRuntime) =>
  (option: RawOption | RawIconOption): Choice<string> =>
    isRawIconOption(option) ? toIconChoice(ctx)(option) : toChoice(ctx)(option);

const selectedFormation = (ctx: FormRuntime) =>
  findFormation({
    themes: ctx.catalog.themes,
    id: Number(ctx.answers.idFormationBitrix),
  });

const financementChoices = (ctx: FormRuntime): Choice<string>[] => {
  const formation = selectedFormation(ctx);
  return financementOptions
    .filter((o) => formation?.has_cpf || o.id !== CPF_FINANCEMENT_CODE)
    .filter(
      (o) =>
        formation?.is_eligible_alternance ||
        o.id !== ALTERNANCE_FINANCEMENT_CODE,
    )
    .map(toChoice(ctx));
};

const domainChoices = (ctx: FormRuntime): Choice<string>[] => [
  ...ctx.catalog.themes
    .filter((theme) => theme.is_visible_in_form_lead)
    .map(
      (theme): Choice<string> => ({
        type: 'emoji',
        label: theme.name,
        value: String(theme.id),
        icon: snakelize(theme.name),
      }),
    ),
  {
    type: 'emojiInput',
    label: 'Autre (Précisez) :',
    value: String(UNCOVERED_DOMAIN_ID),
    icon: 'source_other',
    input: {
      name: 'domainName',
      placeholder: 'Votre domaine',
      value: String(ctx.answers.domainName ?? ''),
      onChange: (value) => ctx.onChange?.({ name: 'domainName', value }),
      validate: (value) => (value ? { valid: true } : { valid: false }),
    },
  },
];

const FORMATION_TYPE_COLOR: Record<
  FormationType,
  NonNullable<ComponentProps<typeof Chip>['color']>
> = {
  DIPLOMA: 'orange',
  CERTIFYING: 'grey',
  ACCELERATED: 'yellow',
};

const trainingChoices = (ctx: FormRuntime): Choice<string>[] => {
  const formations = getDomainFormations({
    themes: ctx.catalog.themes,
    domainId: Number(ctx.answers.domainId),
  });
  const trainings: Choice<string>[] = formations.map((formation) => {
    const tags: ComponentProps<typeof Chip>[] = [];
    if (formation.has_cpf) tags.push({ label: 'CPF', color: 'blue' });
    if (formation.duration)
      tags.push({ label: formation.duration, color: 'green' });
    if (formation.formation_type) {
      tags.push({
        label:
          formationTypes[formation.formation_type as FormationType] ??
          formation.formation_type,
        color: FORMATION_TYPE_COLOR[formation.formation_type as FormationType],
      });
    }
    return {
      type: 'tags',
      label: formation.commercial_name,
      value: String(formation.id_formation_bitrix),
      tags,
    };
  });
  return [
    ...trainings,
    { type: 'plain', label: 'Je souhaite être conseillé(e)', value: 'nothing' },
  ];
};

export const getStepFields = (
  stepKey: StepKey,
  ctx: FormRuntime,
): FieldDefinition[] => {
  switch (stepKey) {
    case 'situationPro':
      return [
        {
          kind: 'choice',
          name: 'situationPro',
          layout: 'column',
          choices: professionalSituationOptions.map(toChoice(ctx)),
        },
      ];
    case 'objectif':
      return [
        {
          kind: 'choice',
          name: 'objectif',
          layout: 'column',
          choices: objectifOptions.map(toChoice(ctx)),
        },
      ];
    case 'domain':
      return [
        {
          kind: 'choice',
          name: 'domainId',
          layout: 'grid',
          choices: domainChoices(ctx),
        },
      ];
    case 'training':
      return [
        {
          kind: 'choice',
          name: 'idFormationBitrix',
          layout: 'column',
          choices: trainingChoices(ctx),
        },
      ];
    case 'niveauEtudes':
      return [
        {
          kind: 'choice',
          name: 'etudes',
          layout: 'column',
          choices: etudesOptions.map(toChoice(ctx)),
        },
      ];
    case 'financement':
      return [
        {
          kind: 'choice',
          name: 'financement',
          layout: 'column',
          choices: financementChoices(ctx),
        },
      ];
    case 'formationMode':
      return [
        {
          kind: 'choice',
          name: 'formationMode',
          layout: 'column',
          choices: formationModeOptions.map(toChoice(ctx)),
        },
      ];
    case 'ideeProjet':
      return [
        {
          kind: 'choice',
          name: 'ideeProjet',
          layout: 'column',
          choices: ideeProjetOptions.map(toFieldChoice(ctx)),
        },
      ];
    case 'typeStructure':
      return [
        {
          kind: 'choice',
          name: 'typeStructure',
          layout: 'column',
          choices: typeStructureOptions.map(toFieldChoice(ctx)),
        },
      ];
    case 'delaiDebut':
      return [
        {
          kind: 'choice',
          name: 'delaiDebut',
          layout: 'column',
          choices: delaiDebutOptions.map(toFieldChoice(ctx)),
        },
      ];
    case 'secteurActivite':
      return [
        {
          kind: 'choice',
          name: 'secteurActivite',
          layout: 'column',
          choices: secteurActiviteOptions.map(toFieldChoice(ctx)),
        },
      ];
    case 'domaineExpertise':
      return [
        {
          kind: 'choice',
          name: 'domaineExpertise',
          layout: 'column',
          choices: domaineExpertiseOptions.map(toFieldChoice(ctx)),
        },
      ];
    case 'transmettreCompetences':
      return [
        {
          kind: 'choice',
          name: 'transmettreCompetences',
          layout: 'column',
          choices: transmettreCompetencesOptions.map(toFieldChoice(ctx)),
        },
      ];
    case 'experienceEsthetique':
      return [
        {
          kind: 'choice',
          name: 'experienceEsthetique',
          layout: 'column',
          choices: experienceEsthetiqueOptions.map(toFieldChoice(ctx)),
        },
      ];
    case 'interetsEsthetique':
      return [
        {
          kind: 'multipleChoice',
          name: 'interetsEsthetique',
          layout: 'column',
          standaloneValues: INTERETS_ESTHETIQUE_EXCLUSIVE,
          choices: interetsEsthetiqueOptions.map(toFieldChoice(ctx)),
        },
      ];
    case 'stageEsthetique':
      return [
        {
          kind: 'choice',
          name: 'stageEsthetique',
          layout: 'column',
          choices: stageOptions.map(toFieldChoice(ctx)),
        },
      ];
    case 'formationPetiteEnfance':
      return [
        {
          kind: 'choice',
          name: 'formationPetiteEnfance',
          layout: 'column',
          choices: formationPetiteEnfanceOptions.map(toFieldChoice(ctx)),
        },
      ];
    case 'stagePetiteEnfance':
      return [
        {
          kind: 'choice',
          name: 'stagePetiteEnfance',
          layout: 'column',
          choices: stageOptions.map(toFieldChoice(ctx)),
        },
      ];
    case 'equipementInfo':
      return [
        {
          kind: 'choice',
          name: 'equipementInfo',
          layout: 'column',
          choices: equipementInfoOptions.map(toFieldChoice(ctx)),
        },
      ];
    case 'niveauAnglais':
      return [
        {
          kind: 'choice',
          name: 'niveauAnglais',
          layout: 'column',
          choices: niveauAnglaisOptions.map(toFieldChoice(ctx)),
        },
      ];
    case 'mailConsent':
      return [
        {
          kind: 'flex',
          name: 'email',
          components: [
            {
              name: 'email',
              inputType: 'email',
              label: 'ADRESSE EMAIL',
              placeholder: 'my@example.com',
            },
          ],
        },
        {
          kind: 'consent',
          name: 'consentPartners',
          required: false,
          label:
            "J'accepte de recevoir par email des offres commerciales et informations de la part de L'École Française",
        },
      ];
    case 'personalConsent':
      return [
        {
          kind: 'choice',
          name: 'gender',
          layout: 'row',
          choices: genderOptions.map(
            (option): Choice<string> => ({
              type: 'radio',
              label: option.name,
              value: option.id,
            }),
          ),
        },
        {
          kind: 'flex',
          name: 'nom_prenom',
          components: [
            {
              name: 'prenom',
              inputType: 'name',
              label: 'PRÉNOM',
              placeholder: 'Prénom',
            },
            {
              name: 'nom',
              inputType: 'name',
              label: 'NOM',
              placeholder: 'Nom',
            },
          ],
        },
        {
          kind: 'flex',
          name: 'numInt',
          components: [
            { name: 'numInt', inputType: 'tel', label: 'TÉLÉPHONE' },
          ],
        },
        {
          kind: 'consent',
          name: 'consentContact',
          required: true,
          label:
            "J'accepte que L'École Française utilise les données renseignées dans ce formulaire pour me contacter et suivre mon dossier",
        },
      ];
    case 'sourceId':
      return [
        {
          kind: 'choice',
          name: 'sourceId',
          layout: 'gridPairs',
          choices: sourcesOptions.map(toChoice(ctx)),
        },
      ];
  }
};
